import React, { useEffect } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { DeviceType, ModalType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import createIcon from '../../asset/icon/create.png';
import { GroupMemberTagData } from '../../shared/dto/EntityRepository';
import editIcon from '../../asset/icon/edit.png';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import {
  useGroupMemberTagActions,
  useGroupMemberTagState,
} from '../../shared/hooks/useApi/useGroupMemberTag';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowGroupMemberTagList } from '../../shared/util/visibility/groupMemberVisibility';

const GroupLabelStation: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { setStandardModal } = useModalStore();
  const { deviceType } = useDeviceStore();
  const {
    tags,
    selectedTags,
    listOwnerAui,
    status,
  } = useGroupMemberTagState();
  const {
    getGroupMemberTagList,
    addSelectedTag,
    removeSelectedTag,
  } = useGroupMemberTagActions();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const { requestExitEditMode } = useUnsavedChanges();
  const canShowTags = canShowGroupMemberTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status,
  });
  const visibleTags = canShowTags ? tags : [];
  const visibleSelectedTags = canShowTags ? selectedTags : [];

  const isMobile = deviceType === DeviceType.MOBILE;

  useEffect(() => {
    void getGroupMemberTagList();
  }, [pageOwner?.aui]);

  const handleTags = () => {
    setStandardModal({
      modalType: ModalType.GROUP_MEMBER_TAG_STATION,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const handleCreateTag = () => {
    setStandardModal({
      modalType: ModalType.CREATE_GROUP_MEMBER_TAG,
      title: 'Create Tag',
      value: '',
      handleChange: () => {},
    });
  };

  const handleSelectTag = (tag: GroupMemberTagData) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }
    if (selectedTags.some((selected) => selected.id === tag.id)) {
      removeSelectedTag(tag);
    } else {
      addSelectedTag(tag);
    }
  };

  const handleSelectTagModal = () => {
    setStandardModal({
      modalType: ModalType.SELECT_GROUP_MEMBER_TAG_MOBILE,
      title: 'Labels',
      value: '',
      handleChange: () => {},
    });
  };

  const hasLabels = visibleTags.length > 0;

  return (
    <GroupLabelStationComp>
      {!isEditMode && hasLabels && isMobile && (
        <MobileLabelBtn onClick={handleSelectTagModal}>
          <LabelBlock>
            Label :
          </LabelBlock>
          {(isMobile && visibleSelectedTags.length > 0) ? (
            <SelectedTagCount> · {getModalMessage('TAG_SELECTED_COUNT', { count: visibleSelectedTags.length })}</SelectedTagCount>
          ) : null}
        </MobileLabelBtn>
      )}
      {isEditMode && (
        <BtnContainer>
          <HintSmallWrapper content={getHintMessage('CHANGE_TAGS')}>
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent
              backgroundImg={editIcon}
              onClick={handleTags}
            />
          </HintSmallWrapper>
          <HintSmallWrapper content={getHintMessage('ADD_TAG')}>
            <UniversalIconBtn
              size="small"
              border={false}
              isTransparent
              backgroundImg={createIcon}
              onClick={handleCreateTag}
            />
          </HintSmallWrapper>
        </BtnContainer>
      )}
      {hasLabels && !isMobile && (
        <GroupLabelFactorContainer>
          {visibleTags.map((tag) => (
            <GroupLabelFactor
              key={tag.id}
              title={tag.name}
              $isSelected={visibleSelectedTags.some((selected) => selected.id === tag.id)}
              onClick={() => handleSelectTag(tag)}
            >
              {tag.name}
            </GroupLabelFactor>
          ))}
        </GroupLabelFactorContainer>
      )}
    </GroupLabelStationComp>
  );
};

export default GroupLabelStation;

const GroupLabelStationComp = styled.section`
  width: fit-content;
  height: calc(2vh);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  z-index: 3;
`;

const GroupLabelFactorContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
`;

const LabelBlock = styled.div`
  width: fit-content;
  height: 12px;
  display: flex;
  align-items: center;
`;

const MobileLabelBtn = styled.div`
  display: flex;
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
`;

const SelectedTagCount = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_01};
  font-style: italic;
  user-select: none;
`;

const GroupLabelFactor = styled.span<{ $isSelected: boolean }>`
  width: fit-content;
  flex-shrink: 0;
  border-bottom: 1px dotted ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_03 : 'transparent'};
  color: ${({ $isSelected, theme }) => $isSelected ? theme.colors.color_Gray_02 : theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;

const BtnContainer = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 4px;
`;
